import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
    FaArrowLeft, FaEdit, FaTrash, FaCheckCircle, FaClock, 
    FaCalendarAlt, FaFlag, FaUser, FaBuilding, FaBell, 
    FaComment, FaPaperPlane, FaDownload, FaPrint,
    FaExclamationTriangle, FaInfoCircle, FaHistory
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const TaskDetails = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [task, setTask] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [activityLog, setActivityLog] = useState([]);

    useEffect(() => {
        fetchTaskDetails();
        fetchComments();
    }, [taskId]);

    const fetchTaskDetails = async () => {
        try {
            const response = await axios.get(`/api/tasks/${taskId}`);
            if (response.data.success) {
                setTask(response.data.task);
                // Generate activity log from task data
                generateActivityLog(response.data.task);
            }
        } catch (error) {
            toast.error('Failed to load task details');
            navigate('/tasks');
        } finally {
            setLoading(false);
        }
    };

    const fetchComments = async () => {
        try {
            const response = await axios.get(`/api/tasks/${taskId}/comments`);
            if (response.data.success) {
                setComments(response.data.comments);
            }
        } catch (error) {
            console.error('Failed to load comments');
        }
    };

    const generateActivityLog = (taskData) => {
        const logs = [];
        
        if (taskData.created_date) {
            logs.push({
                date: taskData.created_date,
                action: 'Task Created',
                user: taskData.assigned_by || 'System',
                details: `Task "${taskData.title}" was created`
            });
        }
        
        if (taskData.updated_date) {
            logs.push({
                date: taskData.updated_date,
                action: 'Task Updated',
                user: taskData.updated_by || 'Unknown',
                details: `Task was updated by ${taskData.updated_by}`
            });
        }
        
        if (taskData.completed_date) {
            logs.push({
                date: taskData.completed_date,
                action: 'Task Completed',
                user: taskData.assigned_name || 'User',
                details: 'Task was marked as completed'
            });
        }
        
        setActivityLog(logs);
    };

    const addComment = async () => {
        if (!newComment.trim()) {
            toast.error('Please enter a comment');
            return;
        }

        setSubmitting(true);
        try {
            const response = await axios.post(`/api/tasks/${taskId}/comments`, {
                comment: newComment
            });
            if (response.data.success) {
                toast.success('Comment added successfully');
                setNewComment('');
                fetchComments();
            }
        } catch (error) {
            toast.error('Failed to add comment');
        } finally {
            setSubmitting(false);
        }
    };

    const updateTaskStatus = async (status) => {
        try {
            await axios.patch(`/api/tasks/${taskId}/status`, { status });
            toast.success(`Task marked as ${status}`);
            fetchTaskDetails();
        } catch (error) {
            toast.error('Failed to update task status');
        }
    };

    const deleteTask = async () => {
        if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
            try {
                await axios.delete(`/api/tasks/${taskId}`);
                toast.success('Task deleted successfully');
                navigate('/tasks');
            } catch (error) {
                toast.error('Failed to delete task');
            }
        }
    };

    const getPriorityColor = (priority) => {
        switch(priority) {
            case 'High': return 'priority-high';
            case 'Medium': return 'priority-medium';
            case 'Low': return 'priority-low';
            default: return 'priority-medium';
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'Completed': return 'status-completed';
            case 'Pending': return 'status-pending';
            default: return 'status-pending';
        }
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'Completed': return <FaCheckCircle className="text-green-500" />;
            case 'Pending': return <FaClock className="text-yellow-500" />;
            default: return <FaClock className="text-gray-500" />;
        }
    };

    const isOverdue = (dueDate) => {
        return new Date(dueDate) < new Date();
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateTime = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const printTask = () => {
        const printContent = document.getElementById('task-details-content');
        const originalContent = document.body.innerHTML;
        document.body.innerHTML = printContent.innerHTML;
        window.print();
        document.body.innerHTML = originalContent;
        window.location.reload();
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="loader"></div>
            </div>
        );
    }

    if (!task) {
        return (
            <div className="p-6">
                <div className="glass-card p-12 text-center">
                    <FaExclamationTriangle className="mx-auto text-5xl text-red-500 mb-3" />
                    <p className="text-gray-500">Task not found</p>
                    <button
                        onClick={() => navigate('/tasks')}
                        className="mt-4 btn-gradient px-4 py-2 rounded-lg text-white"
                    >
                        Back to Tasks
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6" id="task-details-content">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={() => navigate('/tasks')}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                    <FaArrowLeft /> Back to Tasks
                </button>
                <div className="flex gap-3">
                    <button
                        onClick={printTask}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all flex items-center gap-2"
                    >
                        <FaPrint /> Print
                    </button>
                    {user?.role === 'Admin' && task.status !== 'Completed' && (
                        <>
                            <button
                                onClick={() => navigate(`/edit-task/${task.task_id}`)}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2"
                            >
                                <FaEdit /> Edit
                            </button>
                            <button
                                onClick={deleteTask}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center gap-2"
                            >
                                <FaTrash /> Delete
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Task Details Card */}
            <div className="glass-card p-6 mb-6">
                {/* Status Banner */}
                <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
                    task.status === 'Completed' ? 'bg-green-50' : 
                    isOverdue(task.due_date) && task.status === 'Pending' ? 'bg-red-50' : 'bg-blue-50'
                }`}>
                    <div className="flex items-center gap-3">
                        {getStatusIcon(task.status)}
                        <div>
                            <p className="font-semibold">
                                Status: <span className={getStatusColor(task.status)}>{task.status}</span>
                            </p>
                            {task.status === 'Pending' && isOverdue(task.due_date) && (
                                <p className="text-sm text-red-600 mt-1">This task is overdue!</p>
                            )}
                        </div>
                    </div>
                    {task.status === 'Pending' && (
                        <button
                            onClick={() => updateTaskStatus('Completed')}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center gap-2"
                        >
                            <FaCheckCircle /> Mark Complete
                        </button>
                    )}
                </div>

                {/* Task Title */}
                <h1 className="text-3xl font-bold text-gray-800 mb-4">{task.title}</h1>
                
                {/* Task Description */}
                <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-700 mb-2">Description</h2>
                    <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{task.description || 'No description provided'}</p>
                </div>

                {/* Task Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <FaCalendarAlt className="text-primary text-xl" />
                        <div>
                            <p className="text-xs text-gray-500">Due Date</p>
                            <p className={`font-semibold ${isOverdue(task.due_date) && task.status === 'Pending' ? 'text-red-600' : 'text-gray-800'}`}>
                                {formatDate(task.due_date)}
                                {isOverdue(task.due_date) && task.status === 'Pending' && ' (Overdue)'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <FaFlag className="text-primary text-xl" />
                        <div>
                            <p className="text-xs text-gray-500">Priority</p>
                            <span className={getPriorityColor(task.priority)}>{task.priority}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <FaUser className="text-primary text-xl" />
                        <div>
                            <p className="text-xs text-gray-500">Assigned To</p>
                            <p className="font-semibold text-gray-800">{task.assigned_name || task.assigned_to}</p>
                            <p className="text-xs text-gray-500">{task.assigned_to}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <FaBuilding className="text-primary text-xl" />
                        <div>
                            <p className="text-xs text-gray-500">Department</p>
                            <p className="font-semibold text-gray-800">{task.department || 'N/A'}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <FaUser className="text-primary text-xl" />
                        <div>
                            <p className="text-xs text-gray-500">Assigned By</p>
                            <p className="font-semibold text-gray-800">{task.assigned_by || 'N/A'}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <FaClock className="text-primary text-xl" />
                        <div>
                            <p className="text-xs text-gray-500">Frequency</p>
                            <p className="font-semibold text-gray-800">{task.frequency || 'One Time'}</p>
                        </div>
                    </div>

                    {task.completed_date && (
                        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                            <FaCheckCircle className="text-green-500 text-xl" />
                            <div>
                                <p className="text-xs text-gray-500">Completed Date</p>
                                <p className="font-semibold text-green-700">{formatDate(task.completed_date)}</p>
                            </div>
                        </div>
                    )}

                    {task.created_date && (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <FaInfoCircle className="text-primary text-xl" />
                            <div>
                                <p className="text-xs text-gray-500">Created Date</p>
                                <p className="font-semibold text-gray-800">{formatDate(task.created_date)}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Comments Section */}
            <div className="glass-card p-6 mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FaComment /> Comments ({comments.length})
                </h2>

                {/* Add Comment */}
                <div className="mb-6">
                    <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment..."
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                        onClick={addComment}
                        disabled={submitting}
                        className="mt-2 btn-gradient px-4 py-2 rounded-lg text-white flex items-center gap-2"
                    >
                        <FaPaperPlane /> {submitting ? 'Posting...' : 'Post Comment'}
                    </button>
                </div>

                {/* Comments List */}
                <div className="space-y-4 max-h-96 overflow-y-auto">
                    {comments.length > 0 ? (
                        comments.map((comment, index) => (
                            <div key={comment.comment_id || index} className="bg-gray-50 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                        <FaUser className="text-primary" />
                                        <span className="font-semibold text-gray-800">{comment.author}</span>
                                        <span className="text-xs text-gray-500">{formatDateTime(comment.created_at)}</span>
                                    </div>
                                </div>
                                <p className="text-gray-600">{comment.comment}</p>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <FaComment className="mx-auto text-4xl mb-2" />
                            <p>No comments yet</p>
                            <p className="text-sm">Be the first to add a comment</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Activity Log */}
            {activityLog.length > 0 && (
                <div className="glass-card p-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <FaHistory /> Activity Log
                    </h2>
                    <div className="space-y-3">
                        {activityLog.map((log, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                <div className="w-2 h-2 mt-2 rounded-full bg-primary"></div>
                                <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <span className="font-semibold text-gray-800">{log.action}</span>
                                            <p className="text-sm text-gray-600">{log.details}</p>
                                        </div>
                                        <span className="text-xs text-gray-400">{formatDateTime(log.date)}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">By: {log.user}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskDetails;